import { useState, type FormEvent } from "react";
import { en as t } from "../i18n/en";
import { ApiError } from "../lib/api";
import { COLOR_NAMES, PLAYER_COLORS } from "../lib/colors";
import { loadProfile, saveProfile } from "../lib/identity";

const MIN_NAME = 2;
const MAX_NAME = 16;

/** Nickname and colour, used to host or join. */
export function PlayerForm({
  submitLabel,
  onSubmit,
  taken = [],
  initial,
}: {
  submitLabel: string;
  onSubmit: (nickname: string, color: string) => Promise<void>;
  taken?: string[];
  /** Prefill (when editing an existing player); defaults to the saved profile. */
  initial?: { nickname: string; color: string };
}) {
  const profile = initial ?? loadProfile();
  const [name, setName] = useState(profile?.nickname ?? "");
  const [color, setColor] = useState(profile?.color ?? COLOR_NAMES[0]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const nick = name.trim();
    if (nick.length < MIN_NAME || nick.length > MAX_NAME) return setError("Names need 2 to 16 characters.");
    setBusy(true);
    setError("");
    try {
      saveProfile({ nickname: nick, color });
      await onSubmit(nick, color);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
      setBusy(false);
    }
  }

  return (
    <form className="player-form" onSubmit={submit} noValidate>
      <label htmlFor="nick">{t.yourName}</label>
      <input
        id="nick"
        className="text-input"
        value={name}
        onChange={(e) => { setName(e.target.value); setError(""); }}
        placeholder={t.namePlaceholder}
        maxLength={MAX_NAME}
        autoComplete="nickname"
        aria-invalid={!!error}
        aria-describedby={error ? "nick-error" : undefined}
      />
      <fieldset className="colors">
        <legend>{t.pickColour}</legend>
        {COLOR_NAMES.map((c) => (
          <label key={c} className="swatch" style={{ background: PLAYER_COLORS[c].hex }} title={PLAYER_COLORS[c].label}>
            <input type="radio" name="color" value={c} checked={color === c} onChange={() => setColor(c)} />
            <span className="sr-only">{PLAYER_COLORS[c].label}{taken.includes(c) ? " (taken)" : ""}</span>
          </label>
        ))}
      </fieldset>
      {error && <p id="nick-error" className="error">{error}</p>}
      <button type="submit" className="btn-primary" disabled={busy}>
        {busy ? t.loading : submitLabel}
      </button>
    </form>
  );
}
